import React, { useMemo } from 'react';

import { Box, Button, FormHelperText, Slider, Stack, TextField, Typography } from '@mui/material';

import type { LensValueFormat } from '~/types/leLens';

import { formatLensValue } from './lensChart';

export type AxisRangeControlProps = {
  /** The values the card is presently drawing. Enables the visual range picker. */
  valueExtent?: { min: number; max: number };
  /** An empty value means the renderer chooses the lower bound. */
  minValue: string;
  /** An empty value means the renderer chooses the upper bound. */
  maxValue: string;
  /** Stacks and other zero-based charts must not offer a misleading raised floor. */
  allowFloor: boolean;
  disabled: boolean;
  valueFormat: LensValueFormat;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
  /** A reader-facing label, for example “Primary axis” or “Second axis”. */
  label?: string;
  /** Prefixes the field and slider labels where two ranges share a dialog. */
  prefix?: string;
  minLabel?: string;
  maxLabel?: string;
};

type NumericRange = { min: number; max: number; step: number };

function numberFromField(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function fieldValue(value: number): string {
  // Avoid storing display formatting (commas, currency signs) in the exact input.
  return String(Number(value.toPrecision(12)));
}

/** A predictable 1/2/5 step keeps sliders useful at both dollars and millions. */
function stableStep(span: number): number {
  const safeSpan = Math.max(Math.abs(span), 1);
  const raw = safeSpan / 100;
  const power = 10 ** Math.floor(Math.log10(raw));
  const factor = raw / power;
  const rounded = factor <= 1 ? 1 : factor <= 2 ? 2 : factor <= 5 ? 5 : 10;
  return rounded * power;
}

function sliderRange(extent: { min: number; max: number }, allowFloor: boolean): NumericRange {
  const dataMin = Math.min(extent.min, extent.max);
  const dataMax = Math.max(extent.min, extent.max);
  const span = Math.max(dataMax - dataMin, Math.max(Math.abs(dataMin), Math.abs(dataMax), 1) * 0.1, 1);
  const padding = span * 0.1;
  const minimum = allowFloor ? Math.min(dataMin, 0) - padding : 0;
  const maximum = Math.max(dataMax, 0) + padding;
  return { min: minimum, max: maximum, step: stableStep(maximum - minimum) };
}

function clamped(value: number, range: NumericRange): number {
  return Math.min(range.max, Math.max(range.min, value));
}

/**
 * Exact fields remain the source of truth. The slider is a visual companion,
 * never a formatter that could overwrite a value someone is still typing.
 */
export default function AxisRangeControl({
  valueExtent,
  minValue,
  maxValue,
  allowFloor,
  disabled,
  valueFormat,
  onMinChange,
  onMaxChange,
  label = 'Axis range',
  prefix,
  minLabel,
  maxLabel,
}: AxisRangeControlProps) {
  const range = useMemo(
    () => (valueExtent ? sliderRange(valueExtent, allowFloor) : undefined),
    [allowFloor, valueExtent],
  );
  const hasAutomaticValues = !minValue.trim() && !maxValue.trim();
  const fitsData =
    valueExtent !== undefined &&
    maxValue.trim() === fieldValue(valueExtent.max) &&
    (!allowFloor || minValue.trim() === fieldValue(valueExtent.min));
  const mode = hasAutomaticValues ? 'automatic' : fitsData ? 'fit' : 'custom';
  const fieldPrefix = prefix ? `${prefix} ` : '';
  const resolvedMinLabel = minLabel ?? `${fieldPrefix}start axis at`;
  const resolvedMaxLabel = maxLabel ?? `${fieldPrefix}end axis at`;

  const automatic = () => {
    onMinChange('');
    onMaxChange('');
  };

  const fitData = () => {
    if (!valueExtent) {
      return;
    }
    if (allowFloor) {
      onMinChange(fieldValue(valueExtent.min));
    }
    onMaxChange(fieldValue(valueExtent.max));
  };

  const custom = () => {
    if (!valueExtent) {
      if (allowFloor && !minValue.trim()) {
        onMinChange('0');
      }
      return;
    }
    if (allowFloor && !minValue.trim()) {
      onMinChange(fieldValue(valueExtent.min));
    }
    if (!maxValue.trim()) {
      onMaxChange(fieldValue(valueExtent.max));
    }
  };

  const sliderValue = (() => {
    if (!range || !valueExtent) {
      return undefined;
    }
    const max = clamped(numberFromField(maxValue) ?? valueExtent.max, range);
    if (!allowFloor) {
      return max;
    }
    const min = clamped(numberFromField(minValue) ?? valueExtent.min, range);
    return min <= max ? [min, max] : [max, min];
  })();

  return (
    <Box aria-label={label} component="section" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle2">{label}</Typography>
          <Typography color="text.secondary" variant="body2">
            {allowFloor
              ? 'Set an exact range when the comparison needs it. A raised start is clearly marked on the chart.'
              : 'This chart always starts at zero so stacked values remain truthful. You can set only the upper bound.'}
          </Typography>
        </Box>

        <Stack aria-label={`${fieldPrefix}axis range choices`} direction="row" flexWrap="wrap" gap={1} role="group">
          <Button
            aria-pressed={mode === 'automatic'}
            disabled={disabled}
            onClick={automatic}
            size="small"
            variant={mode === 'automatic' ? 'contained' : 'outlined'}
          >
            Automatic
          </Button>
          {valueExtent && (
            <Button
              aria-pressed={mode === 'fit'}
              disabled={disabled}
              onClick={fitData}
              size="small"
              variant={mode === 'fit' ? 'contained' : 'outlined'}
            >
              Fit data
            </Button>
          )}
          <Button
            aria-pressed={mode === 'custom'}
            disabled={disabled}
            onClick={custom}
            size="small"
            variant={mode === 'custom' ? 'contained' : 'outlined'}
          >
            Custom
          </Button>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {allowFloor ? (
            <TextField
              disabled={disabled}
              fullWidth
              inputProps={{ inputMode: 'decimal', step: 'any' }}
              label={resolvedMinLabel}
              onChange={(event) => onMinChange(event.target.value)}
              size="small"
              value={minValue}
            />
          ) : (
            <Box
              aria-label={`${fieldPrefix}axis starts at zero`}
              sx={{
                alignItems: 'center',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                flex: 1,
                px: 1.5,
              }}
            >
              <Typography color="text.secondary" variant="body2">
                Starts at 0
              </Typography>
            </Box>
          )}
          <TextField
            disabled={disabled}
            fullWidth
            inputProps={{ inputMode: 'decimal', step: 'any' }}
            label={resolvedMaxLabel}
            onChange={(event) => onMaxChange(event.target.value)}
            size="small"
            value={maxValue}
          />
        </Stack>

        {valueExtent && range && sliderValue !== undefined && (
          <Box sx={{ px: 1 }}>
            <Slider
              disabled={disabled}
              getAriaLabel={(index) =>
                allowFloor
                  ? `${fieldPrefix}axis range ${index === 0 ? 'start' : 'end'}`
                  : `${fieldPrefix}axis range maximum`
              }
              getAriaValueText={(value) => formatLensValue(value, valueFormat)}
              max={range.max}
              min={range.min}
              onChange={(_event, nextValue) => {
                if (Array.isArray(nextValue)) {
                  onMinChange(fieldValue(nextValue[0]));
                  onMaxChange(fieldValue(nextValue[1]));
                } else {
                  onMaxChange(fieldValue(nextValue));
                }
              }}
              step={range.step}
              value={sliderValue}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => formatLensValue(value, valueFormat)}
            />
            <FormHelperText sx={{ mt: 0 }}>
              Data on this card: {formatLensValue(valueExtent.min, valueFormat)} to{' '}
              {formatLensValue(valueExtent.max, valueFormat)}
            </FormHelperText>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
